import { ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

interface MonthPickerProps {
  initialDate?: Date | string;
  onChange?: (month: string, year?: string) => void;
  onClose?: () => void;
}

export default function MonthPicker({
  initialDate,
  onChange,
  onClose,
}: MonthPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMoment, setSelectedMoment] = useState(() => {
    if (initialDate) {
      const m = moment(initialDate, moment.ISO_8601, true);
      if (m.isValid()) return m;
      // fallback: try parsing as month number
      if (
        typeof initialDate === "string" &&
        !isNaN(Number(initialDate)) &&
        Number(initialDate) >= 1 &&
        Number(initialDate) <= 12
      ) {
        return moment()
          .month(Number(initialDate) - 1)
          .startOf("month");
      }
    }
    return moment().startOf("month");
  });

  const [currentYear, setCurrentYear] = useState(selectedMoment.year());
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialDate) {
      const m = moment(initialDate, moment.ISO_8601, true);
      if (m.isValid()) {
        setSelectedMoment(m);
        setCurrentYear(m.year());
        return;
      }
      if (
        typeof initialDate === "string" &&
        !isNaN(Number(initialDate)) &&
        Number(initialDate) >= 1 &&
        Number(initialDate) <= 12
      ) {
        const newMoment = moment()
          .year(currentYear)
          .month(Number(initialDate) - 1)
          .startOf("month");
        setSelectedMoment(newMoment);
        setCurrentYear(newMoment.year());
        return;
      }
    }
  }, [initialDate]);

  const months = moment.monthsShort();

  const handleYearChange = (increment: number) => {
    const newYear = selectedMoment.year() + increment;
    setCurrentYear(newYear);
    const newMoment = selectedMoment.clone().year(newYear);
    setSelectedMoment(newMoment);
  };

  const handleMonthSelect = (monthIndex: number) => {
    const newMoment = selectedMoment.clone().month(monthIndex).startOf("month");
    setSelectedMoment(newMoment);
    setCurrentYear(newMoment.year());
    onChange?.(
      (newMoment.month() + 1).toString().padStart(2, "0"),
      newMoment.year().toString()
    );
    setIsOpen(false);
  };

  const handleBlur = (e: React.FocusEvent) => {
    const currentTarget = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      setIsOpen(false);
      onClose?.();
    }
  };

  return (
    <div className="relative" tabIndex={0} onBlur={handleBlur}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-center text-gray-900 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 disabled:pointer-events-none disabled:opacity-50 border border-gray-300 bg-white shadow-sm hover:bg-gray-50 hover:text-gray-900 h-8 px-2 ${
          onClose ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <ChevronLeft
          className="h-4 w-4 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleMonthSelect(selectedMoment.month() - 1);
          }}
        />
        {`${months[selectedMoment.month()]} ${selectedMoment.year()}`}
        <ChevronRight
          className="h-4 w-4 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleMonthSelect(selectedMoment.month() + 1);
          }}
        />
        <span className="sr-only">Open month picker</span>
      </button>

      {isOpen && (
        <div
          ref={popupRef}
          className="absolute mt-1 bg-white rounded-md shadow-lg border border-gray-300 z-[9999] right-0"
          style={{ width: "180px" }}
        >
          {/* Year selector */}
          <div className="flex items-center justify-between p-2">
            <button
              onClick={() => handleYearChange(-1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              &#171;
            </button>
            <span className="text-center text-gray-900 text-xs font-normal">
              {currentYear}
            </span>
            <button
              onClick={() => handleYearChange(1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              &#187;
            </button>
          </div>

          {/* Months grid */}
          <div className="grid grid-cols-3 gap-1 p-2">
            {months.map((month, index) => {
              const isSelected =
                selectedMoment.month() === index &&
                selectedMoment.year() === currentYear;

              return (
                <button
                  key={month}
                  onClick={() => handleMonthSelect(index)}
                  className={`
                    py-1 px-2 text-sm rounded
                    ${isSelected ? "bg-gray-200" : "hover:bg-gray-100"}
                  `}
                >
                  {month}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
